import React from 'react';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  MonitorUp,
  MonitorX,
  PhoneOff,
  MessageSquare,
} from 'lucide-react';

interface MeetingControlsProps {
  isAudioEnabled: boolean;
  isVideoEnabled: boolean;
  isScreenSharing: boolean;
  canScreenShare: boolean;
  onToggleAudio: () => void;
  onToggleVideo: () => void;
  onToggleScreenShare: () => void;
  onLeaveMeeting: () => void;
  onOpenChat?: () => void;
  onEndMeeting?: () => void;
}

export const MeetingControls: React.FC<MeetingControlsProps> = ({
  isAudioEnabled,
  isVideoEnabled,
  isScreenSharing,
  canScreenShare,
  onToggleAudio,
  onToggleVideo,
  onToggleScreenShare,
  onLeaveMeeting,
  onOpenChat,
  onEndMeeting
}) => {
  const btnClass = (active: boolean, danger: boolean = false) => {
    const base = 'px-4 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-all';
    if (danger) return `${base} ${active ? 'bg-red-500 hover:bg-red-600' : 'bg-red-600 hover:bg-red-700'} text-white`;
    if (active) return `${base} bg-primary-500 hover:bg-primary-600 text-white`;
    return `${base} bg-gray-100 hover:bg-gray-200 text-gray-900`;
  };

  const mobileBtnClass = (active: boolean, danger: boolean = false) => {
    let classes = 'flex flex-col items-center gap-1 p-2 rounded-xl transition-all min-w-[56px] ';
    classes += danger || !active ? 'text-white' : 'text-white';
    return classes;
  };

  const mobileIconClass = (active: boolean, danger: boolean = false, special: boolean = false) => {
    let classes = 'w-12 h-12 rounded-full flex items-center justify-center transition-all ';
    if (danger) classes += 'bg-red-500';
    else if (special) classes += active ? 'bg-primary-500' : 'bg-white/10';
    else classes += active ? 'bg-white/10' : 'bg-red-500';
    return classes;
  };

  return (
    <>
      {/* Desktop Controls */}
      <div className="hidden md:block bg-white border-t border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-2">
            <button data-tour="audio-button" onClick={onToggleAudio} className={btnClass(!isAudioEnabled, !isAudioEnabled)}>
              {isAudioEnabled ? <Mic size={20} /> : <MicOff size={20} />}
              <span>{isAudioEnabled ? 'Mute' : 'Unmute'}</span>
            </button>
            <button onClick={onToggleVideo} className={btnClass(!isVideoEnabled, !isVideoEnabled)}>
              {isVideoEnabled ? <Video size={20} /> : <VideoOff size={20} />}
              <span>{isVideoEnabled ? 'Stop Video' : 'Start Video'}</span>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              data-tour="screen-share-button"
              onClick={onToggleScreenShare}
              disabled={!canScreenShare && !isScreenSharing}
              className={`${btnClass(isScreenSharing)} ${(!canScreenShare && !isScreenSharing) ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isScreenSharing ? <MonitorX size={20} /> : <MonitorUp size={20} />}
              <span>{isScreenSharing ? 'Stop Sharing' : 'Share Screen'}</span>
            </button>
            {onOpenChat && (
              <button onClick={onOpenChat} className={btnClass(false)}>
                <MessageSquare size={20} />
                <span>Chat</span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2">
            <button onClick={onLeaveMeeting} className={btnClass(false, true)}>
              <PhoneOff size={20} />
              <span>Leave</span>
            </button>
            {onEndMeeting && (
              <button onClick={onEndMeeting} className="px-4 py-2.5 rounded-lg font-medium flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white transition-all">
                <PhoneOff size={20} />
                <span>End Meeting</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Controls */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-neutral-900/95 backdrop-blur-lg border-t border-white/10 px-4 py-3 z-50">
        <div className="flex items-center justify-around max-w-lg mx-auto">
          <button data-tour="audio-button" onClick={onToggleAudio} className={mobileBtnClass(isAudioEnabled)}>
            <div className={mobileIconClass(isAudioEnabled)}>
              {isAudioEnabled ? <Mic size={22} /> : <MicOff size={22} />}
            </div>
            <span className="text-[10px] font-medium">{isAudioEnabled ? 'Mute' : 'Unmute'}</span>
          </button>

          <button onClick={onToggleVideo} className={mobileBtnClass(isVideoEnabled)}>
            <div className={mobileIconClass(isVideoEnabled)}>
              {isVideoEnabled ? <Video size={22} /> : <VideoOff size={22} />}
            </div>
            <span className="text-[10px] font-medium">Video</span>
          </button>

          <button
            data-tour="screen-share-button"
            onClick={onToggleScreenShare}
            disabled={!canScreenShare && !isScreenSharing}
            className={`${mobileBtnClass(isScreenSharing)} ${(!canScreenShare && !isScreenSharing) ? 'opacity-40' : ''}`}
          >
            <div className={mobileIconClass(isScreenSharing, false, true)}>
              {isScreenSharing ? <MonitorX size={22} /> : <MonitorUp size={22} />}
            </div>
            <span className="text-[10px] font-medium">Share</span>
          </button>

          {onOpenChat && (
            <button onClick={onOpenChat} className={mobileBtnClass(false)}>
              <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                <MessageSquare size={22} />
              </div>
              <span className="text-[10px] font-medium">Chat</span>
            </button>
          )}

          <button onClick={onLeaveMeeting} className={mobileBtnClass(false, true)}>
            <div className={mobileIconClass(false, true)}>
              <PhoneOff size={22} />
            </div>
            <span className="text-[10px] font-medium">Leave</span>
          </button>
        </div>
      </div>
    </>
  );
};